<template>
  <v-card flat color="transparent">
    <v-layout>
      <v-flex>
        <vc-date-picker :attributes="attributes" v-if="!disabled" v-model="valueObject" mode="range" :columns="numColumns" :available-dates="{ start: minDate, end: maxDate }" :popover="{ visibility: 'click' }">
          <v-layout wrap>
            <v-flex :md12="twoLine" :md6="!twoLine" style="min-width: 100px;">
              <v-text-field @update:error="onRuleUpdate('start', $event)" v-model="prettyStartDate" :rules="rules.start" :label="label.start" :placeholder="startPlaceholder" :hide-details="hideDetails" :box="box" :solo="solo" :class="{ 'rounded-left': rounded, 'rounded-right' : twoLine }" prepend-inner-icon="date_range" readonly>
                <template v-if="value.start" v-slot:append>
                  <v-tooltip bottom :open-delay="500" :close-delay="0">
                    <v-btn v-if="!noArrows" small icon class="ma-0 mr-1" :disabled="disabled || hasPreviousStartDate || !startMoment" @click.stop="previousStartDate()" slot="activator">
                      <v-icon>chevron_left</v-icon>
                    </v-btn>
                    <span>Previous day</span>
                  </v-tooltip>
                  <v-tooltip bottom :open-delay="500" :close-delay="0">
                    <v-btn v-if="!noArrows" small icon class="ma-0" :disabled="disabled || !hasNextStartDate || !startMoment" @click.stop="nextStartDate()" slot="activator">
                      <v-icon>chevron_right</v-icon>
                    </v-btn>
                    <span>Next day</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-flex>
            <v-flex :md12="twoLine" :md6="!twoLine" style="min-width: 100px;">
              <v-text-field @update:error="onRuleUpdate('end', $event)" v-model="prettyEndDate" :rules="rules.end" :label="label.end" :placeholder="endPlaceholder" :hide-details="hideDetails" :box="box" :solo="solo" :class="{ 'rounded-right': rounded, 'rounded-left' : twoLine }" :prepend-inner-icon="twoLine ? 'date_range' : null" readonly>
                <template v-if="endMoment" v-slot:append>
                  <v-tooltip bottom :open-delay="500" :close-delay="0">
                    <v-btn v-if="!noArrows" small icon class="ma-0 mr-1" :disabled="disabled || hasPreviousEndDate || !endMoment" @click.stop="previousEndDate()" slot="activator">
                      <v-icon>chevron_left</v-icon>
                    </v-btn>
                    <span>Previous day</span>
                  </v-tooltip>

                  <v-tooltip bottom :open-delay="500" :close-delay="0">
                    <v-btn v-if="!noArrows" small icon class="ma-0 mr-1" :disabled="disabled || !hasNextEndDate || !endMoment" @click.stop="nextEndDate()" slot="activator">
                      <v-icon>chevron_right</v-icon>
                    </v-btn>
                    <span>Next day</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-flex>
          </v-layout>
        </vc-date-picker>
        <template v-else>
          <v-layout>
            <v-flex :md12="twoLine" :md6="!twoLine" style="min-width: 100px;">
              <v-text-field @update:error="onRuleUpdate('start', $event)" disabled v-model="prettyStartDate" :rules="rules.start" :label="label.start" :placeholder="startPlaceholder" :hide-details="hideDetails" :box="box" :solo="solo" :class="{ 'rounded-left': rounded, 'rounded-right' : twoLine}" prepend-inner-icon="date_range" readonly>
                <template v-if="startMoment && !noArrows" v-slot:append>
                  <v-btn small icon class="ma-0 mr-1" disabled>
                    <v-icon>chevron_left</v-icon>
                  </v-btn>
                  <v-btn small icon class="ma-0" disabled>
                    <v-icon>chevron_right</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-flex>
            <v-flex :md12="twoLine" :md6="!twoLine" style="min-width: 100px;">
              <v-text-field @update:error="onRuleUpdate('end', $event)" disabled v-model="prettyEndDate" :rules="rules.end" :label="label.end" :placeholder="endPlaceholder" :hide-details="hideDetails" :box="box" :solo="solo" :class="{ 'rounded-right': rounded, 'rounded-left' : twoLine }" :prepend-inner-icon="twoLine ? 'date_range' : null" readonly>
                <template v-if="endMoment && !noArrows" v-slot:append>
                  <v-btn small icon class="ma-0 mr-1" disabled>
                    <v-icon>chevron_left</v-icon>
                  </v-btn>
                  <v-btn small icon class="ma-0 mr-1" disabled>
                    <v-icon>chevron_right</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-flex>
          </v-layout>
        </template>
      </v-flex>
    </v-layout>
    <v-layout :class="{ 'no-rules': noRules || !hasErrors }">
      <v-menu :disabled="disabled" offset-y>
        <v-tooltip bottom :open-delay="500" :close-delay="0" slot="activator">
          <v-btn class="dropdown-btn mr-0" slot="activator">
            <span class="success--text subheading">
              <span v-if="shortcutPrefix">
                <span class="text-capitalize">
                  {{ shortcutPrefix }}
                </span>
                <span class="text-lowercase">
                  {{ _selectedRange.name(startMoment, endMoment) }}
                </span>
              </span>
              <span v-else>
                {{ _selectedRange.name(startMoment, endMoment) }}
              </span>
            </span>
            <v-icon>arrow_drop_down</v-icon>
          </v-btn>
          <span>Date range shortcuts</span>
        </v-tooltip>
        <v-list dense>
          <template v-for="(item, index) in shownShortcuts">
            <v-divider v-if="item.divider" :key="index" />
            <v-list-tile v-else :key="index" @click="shortcutHandler(item)">
              <v-list-tile-title>{{ item.name(startMoment, endMoment) }}</v-list-tile-title>
            </v-list-tile>
          </template>
        </v-list>
      </v-menu>

      <v-tooltip v-if="!hideClear && startMoment && endMoment && !_selectedRange.hideClear" bottom :open-delay="500" :close-delay="0">
        <v-btn :disabled="disabled" icon class="ml-2 box-shadow" slot="activator" @click="shortcutHandler(shortcutsIndex.all)">
          <v-icon color="error">
            clear
          </v-icon>
        </v-btn>
        <span>Clear date range filter</span>
      </v-tooltip>

      <template v-if="_selectedRange.increment && _selectedRange.decrement">
        <SecondaryBtn @click="selectedRangeDecrement()" noIconPadding class="min-width-none mr-0 ml-3" color="info" icon="chevron_left" />
        <SecondaryBtn @click="selectedRangeIncrement()" noIconPadding class="min-width-none" color="info" icon="chevron_right" />
      </template>
    </v-layout>
    <div v-if="showAvailableText">
      <v-layout v-if="availableDateRange.start || availableDateRange.end" class="pt-3 pb-4">
        <v-flex class="text-xs-center caption">
          Available {{ dataType }}
          <template v-if="availableDateRange.start">from</template>
          <template v-else-if="availableDateRange.end">until</template>
          {{ prettyAvailableStart }}
          <template v-if="availableDateRange.start && availableDateRange.end">&ndash;</template>
          {{ prettyAvailableEnd }}
        </v-flex>
      </v-layout>
    </div>
  </v-card>
</template>

<script>
  import moment from 'moment'
  import { prettyDateFormat } from '@/components/mixins/MomentUtil'
  import { makeShortcuts } from '@/helpers/dateRange'
  import { isValidDate } from '@/helpers/dates'

  import SecondaryBtn from '@/components/generic/buttons/SecondaryBtn'
  const defaultShortcutKey = 'pastThreeMonths'
  export default {
    components: {
      SecondaryBtn,
    },
    props: {
      availableDateRange: {
        type: Object,
        default: () => ({
          start: null,
          end: null,
        }),
      },
      value: Object,
      index: Number,
      disabled: Boolean,
      noArrows: Boolean,
      minDate: {
        type: Date,
        default: null,
      },
      maxDate: {
        type: Date,
        default: null,
      },
      placeholder: {
        type: Object,
        default: () => ({
          start: null,
          end: null,
        }),
      },
      rules: {
        type: Object,
        default: () => ({
          start: [],
          end: [],
        }),
      },
      hideDetails: {
        type: Boolean,
        default: false,
      },
      rounded: {
        type: Boolean,
        default: false,
      },
      minMaxPlaceholder: {
        type: String,
        default: null,
      },
      shortcutPrefix: {
        type: String,
        default: null,
      },
      shortcuts: {
        type: Object,
        default: null,
      },
      includeDefaultShortcuts: {
        type: Boolean,
        default: true,
      },
      relativeDate: {
        type: Date,
        default: null,
      },
      data: {
        type: Array,
        default: null,
      },
      solo: {
        type: Boolean,
        default: false,
      },
      box: {
        type: Boolean,
        default: false,
      },
      label: {
        type: Object,
        default: () => ({
          start: 'From',
          end: 'To',
        }),
      },
      isUTC: {
        type: Boolean,
        default: false,
      },
      isLocal: {
        type: Boolean,
        default: false,
      },
      utcOffset: {
        type: Number,
        default: 0,
      },
      startToEndOfDay: {
        type: Boolean,
        default: false,
      },
      dataType: {
        type: String,
        default: 'data',
      },
      numColumns: {
        type: Number,
        default: 2,
      },
      twoLine: {
        type: Boolean,
        default: false,
      },
      showAvailableText: {
        type: Boolean,
        default: true,
      },
      shortcutKey: {
        type: String,
        default: 'pastMonth',
      },
      loadingValue: {
        type: Boolean,
        default: true,
      },
      hideClear: {
        type: Boolean,
        default: false,
      },
      selectedRange: {
        type: Object,
        default: () => null,
      },
      calculateSelectedRange: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        showHelp: false,
        fab: false,
        customRangeDialog: false,
        restrictions: {
          end: this.makeMoment()
            .startOf('day')
            .toDate(),
        },
        modal: false,
        ruleTrigger: {
          start: false,
          end: false,
        },
      }
    },
    computed: {
      attributes() {
        if (this.data && this.data.length) {
          return [{
            dot: 'red',
            dates: this.data,
          }]
        }
        return null
      },
      shortcutsIndex() {
        let _shortcuts = this.includeDefaultShortcuts ? makeShortcuts(this.makeMoment) : {}

        if (this.shortcuts) {
          for (const [key, value] of Object.entries(this.shortcuts)) {
            _shortcuts[key] = {
              ..._shortcuts[key],
              ...value,
            }
          }
        }

        return _shortcuts
      },
      shownShortcuts() {
        return this._shortcuts.filter(shortcut => !shortcut.hidden)
      },
      _shortcuts() {
        const sortByIndex = (a, b) => a.index > b.index ? 1 : -1
        let result = Object.values(this.shortcutsIndex).sort(sortByIndex)

        return result
      },
      _selectedRange() {
        let result = (this.calculateSelectedRange ? this.calculatedSelectedRange : this.selectedRange)
        return result || this.shortcutsIndex[defaultShortcutKey]
      },
      startPlaceholder() {
        if (!this.minMaxPlaceholder || !this.minDate || !isValidDate(this.minDate)) return ''
        return this.makeMoment(this.minDate).format(this.minMaxPlaceholder)
      },
      endPlaceholder() {
        if (!this.minMaxPlaceholder || !this.maxDate || !isValidDate(this.maxDate)) return ''
        else return this.makeMoment(this.maxDate).format(this.minMaxPlaceholder)
      },
      valueObject: {
        get() {
          return this.value
        },
        set(value) {
          this.emitUpdate(value)
        },
      },
      calculatedSelectedRange() {
        return this._shortcuts.find(shortcut => (
          shortcut.isMatch && shortcut.isMatch(this.startMoment, this.endMoment,
            this.availableDateRangeMoment))) || {
              name: () => 'None',
            }
      },
      startMoment() {
        return this.value.start ? this.makeMoment(this.value.start) : null
      },
      endMoment() {
        return this.value.end ? this.makeMoment(this.value.end) : null
      },
      availableDateRangeMoment() {
        let start = this.availableDateRange.start
        let end = this.availableDateRange.end
        return {
          start: start ? this.makeMoment(start) : null,
          end: end ? this.makeMoment(end) : null,
        }
      },
      prettyStartDate: {
        get() {
          if (!this.startMoment || !this.startMoment._isValid) return ''
          return this.startMoment.format(prettyDateFormat)
        },
        set() {},
      },
      prettyEndDate: {
        get() {
          if (!this.endMoment || !this.endMoment._isValid) return ''
          return this.endMoment.format(prettyDateFormat)
        },
        set() {},
      },
      hasPreviousStartDate() {
        if (!this.startMoment) return false
        if (!this.minDate) return true

        let startRestriction = this.makeMoment(this.minDate)
        return this.startMoment.isAfter(startRestriction, 'days')
      },
      hasNextStartDate() {
        if (!this.startMoment) return false
        if (!this.maxDate) return true

        let endRestriction = this.makeMoment(this.maxDate)
        return this.startMoment.isBefore(endRestriction, 'days')
      },
      hasPreviousEndDate() {
        if (!this.endMoment) return false
        if (!this.minDate) return true

        let startRestriction = this.makeMoment(this.minDate)
        return this.endMoment.isAfter(startRestriction, 'days')
      },
      hasNextEndDate() {
        if (!this.endMoment) return false
        if (!this.maxDate) return true

        let endRestriction = this.makeMoment(this.restrictions.end)
        return this.endMoment.isBefore(endRestriction, 'days')
      },
      prettyAvailableStart() {
        if (!this.availableDateRange.start) return ''
        return this.prettyDate(this.availableDateRange.start)
      },
      prettyAvailableEnd() {
        if (!this.availableDateRange.end) return ''
        return this.prettyDate(this.availableDateRange.end)
      },
      noRules() {
        let rules = this.rules
        return (!rules.start || rules.start.length === 0) && (!rules.end || rules.end.length === 0)
      },
      hasErrors() {
        return this.ruleTrigger.start || this.ruleTrigger.end
      },
      currentRange() {
        if (!this.startMoment || !this.endMoment) return 1
        return this.endMoment.diff(this.startMoment, 'days') || 1
      },
    },
    watch: {
      _selectedRange: {
        handler(value) {
          this.$emit('update:selectedRange', value)
        },
      },
      startMoment: {
        immediate: true,
        handler(value) {
          if (!value) return
          if (value.isAfter(this.endMoment, 'days')) {
            let date = value.toDate()
            this.emitUpdate({
              start: date,
              end: date,
            })
          }
        },
      },
      endMoment: {
        immediate: true,
        handler(value) {
          if (!value) return
          if (value.isBefore(this.startMoment, 'days')) {
            let date = value.toDate()
            this.emitUpdate({
              end: date,
              start: date,
            })
          }
        },
      },
      loadingValue() {
        if (this.shortcutKey && !this.loadingValue) {
          let filter = this._shortcuts.find(x => x.key == this.shortcutKey)
          this.shortcutHandler(filter)
        }
      },
    },
    methods: {
      makeDefaultShortcuts() {
        return makeShortcuts(this.makeMoment())
      },
      makeMoment(date) {
        let result
        if (this.isUTC) {
          result = moment.utc(date).utcOffset(this.utcOffset, true)
        } else {
          result = moment(date)
        }

        if (this.isLocal) {
          return result.local()
        } else {
          return result
        }
      },
      shortcutHandler(shortcut) {
        if (!this.calculateSelectedRange) {
          this.$emit('update:selectedRange', shortcut)
          // return
        }

        let startMoment = this.relativeDate ? this.makeMoment(this.relativeDate) : this.makeMoment()
        let endMoment = startMoment.clone()

        const { start, end } = shortcut.make(startMoment, endMoment)

        let startDate = start ? start.toDate() : null
        let endDate = end ? end.toDate() : null

        this.emitUpdate({
          start: startDate,
          end: endDate,
        })
      },
      selectedRangeIncrement() {
        let startMoment = this.startMoment.clone()
        let endMoment = startMoment.clone()
        const { start, end } = this._selectedRange.increment(startMoment, endMoment)

        let startDate = start ? start.toDate() : null
        let endDate = end ? end.toDate() : null

        this.emitUpdate({
          start: startDate,
          end: endDate,
        })
      },
      selectedRangeDecrement() {
        let startMoment = this.startMoment.clone()
        let endMoment = startMoment.clone()
        const { start, end } = this._selectedRange.decrement(startMoment, endMoment)

        let startDate = start ? start.toDate() : null
        let endDate = end ? end.toDate() : null

        this.emitUpdate({
          start: startDate,
          end: endDate,
        })
      },
      onRuleUpdate(key, value) {
        this.ruleTrigger[key] = value
      },
      emitUpdate(value) {
        if (this.startToEndOfDay) {
          if (value.start) {
            value.start = this.makeMoment(value.start).startOf('day').toDate()
          }

          if (value.end) {
            value.end = this.makeMoment(value.end).endOf('day').toDate()
          }
        }

        this.$emit('input', value)
      },
      prettyDate(date) {
        if (!date) return
        return this.makeMoment(date).format(prettyDateFormat)
      },
      nextStartDate() {
        this.emitUpdate({
          ...this.value,
          start: this.startMoment
            .clone()
            .add(1, 'days')
            .toDate(),
        })
      },
      previousStartDate() {
        this.emitUpdate({
          ...this.value,
          start: this.startMoment && this.startMoment
            .clone()
            .add(-1, 'days')
            .toDate(),
        })
      },
      nextEndDate() {
        this.emitUpdate({
          ...this.value,
          end: this.endMoment && this.endMoment
            .clone()
            .add(1, 'days')
            .toDate(),
        })
      },
      previousEndDate() {
        this.emitUpdate({
          ...this.value,
          end: this.endMoment
            .clone()
            .add(-1, 'days')
            .toDate(),
        })
      },
      prevRange() {
        let dayDiff = this.currentRange
        this.emitUpdate({
          ...this.value,
          start: this.startMoment && this.startMoment
            .clone()
            .add(-dayDiff, 'days')
            .toDate(),
          end: this.endMoment && this.endMoment
            .clone()
            .add(-dayDiff, 'days')
            .toDate(),
        })
      },
      nextRange() {
        let dayDiff = this.currentRange
        this.emitUpdate({
          ...this.value,
          start: this.startMoment && this.startMoment
            .clone()
            .add(dayDiff, 'days')
            .toDate(),
          end: this.endMoment && this.endMoment
            .clone()
            .add(dayDiff, 'days')
            .toDate(),
        })
      },
    },
  }
</script>

<style scoped>
  .date-action:hover {
    color: #333;
  }

  .no-rules {
    margin-top: -20px;
  }
</style>
<style>
  .dropdown-btn>.v-btn__content {
    font-weight: normal !important;
    text-transform: initial !important;
  }

  /* ie 11 only */
  @media screen and (-ms-high-contrast: active),
  screen and (-ms-high-contrast: none) {
    .vc-container .vc-grid-container {
      width: 100%;
      display: -ms-grid;
    }

    .vc-container .vc-grid-container.vc-weeks {
      padding: 5px 6px 7px 6px;
    }

    .vc-container .vc-grid-container .vc-day-content {
      min-height: 1.8rem;
      width: 1.8rem;
      height: 1.8rem;
      margin: 0.1rem auto;
    }

    .vc-highlight {
      height: 100%;
      width: 100%;
    }

    .vc-rounded-full {
      border-radius: 0px;
    }

    .vc-day-content:hover {
      background-color: #eee !important;
      color: #2a4365 !important;
    }

    .vc-opacity-0 {
      visibility: hidden;
    }

    .vc-container .vc-grid-container .vc-day-content {
      width: 22px !important;
      height: 22px !important;
      min-height: 0px;
    }
  }
</style>